import { Component, inject, Input, OnInit } from '@angular/core';
import { ToolbarButtonComponent } from '../../molecules/toolbar-button/toolbar-button.component';
import { AsyncPipe, NgClass, NgForOf, NgIf } from '@angular/common';
import { ToolbarItem } from '../../../models/toolbar-item';
import { SidebarService } from '../../../services/menu/sidebar.service';
import { ActivatedRoute } from '@angular/router';
import { ActiveUserStore } from '../../../stores/user/active-user.state';

@Component({
  selector: 'app-toolbar',
  standalone: true,
  imports: [ToolbarButtonComponent, NgClass, NgIf, NgForOf, AsyncPipe],
  templateUrl: './toolbar.component.html',
  styleUrl: './toolbar.component.scss',
})
export class ToolbarComponent implements OnInit {
  @Input() backLink = '';
  @Input() toolbarItems: ToolbarItem[] | undefined;
  readonly activeUserStore = inject(ActiveUserStore);

  constructor(protected sidebarService: SidebarService) {}

  public navigationSize = 'sm';

  /**
   * Toggles the navigation size between 'sm' (small) and 'md' (medium).
   * Updates the navigation size and triggers the appropriate size-changing actions.
   *
   * @return {void} Does not return any value.
   */
  toggleNavigation(): void {
    this.navigationSize = this.navigationSize == 'sm' ? 'md' : 'sm';
    this.setNavigationSize();
  }

  /**
   * Updates the navigation size by adjusting the toolbar size based on the current navigation size state.
   * If the navigation size is 'sm', the toolbar size is set to 'w-4rem'. Otherwise, it is set to 'w-17rem'.
   *
   * @return {void} This method does not return a value.
   */
  setNavigationSize(): void {
    this.sidebarService.setToolbarSize(
      this.navigationSize === 'sm' ? 'w-4rem' : 'w-17rem'
    );
  }

  /**
   * A lifecycle hook that is called after Angular has initialized all data-bound properties of a component.
   * It sets the navigation size and filters the toolbar items to ensure proper initialization of the component's UI.
   *
   * @return {void} This method does not return a value.
   */
  ngOnInit(): void {
    this.setNavigationSize();
    this.filterToolbarItems();
  }

  /**
   * Filters the list of toolbar items based on the active user's permissions.
   * Modifies the `toolbarItems` property by removing items that the user does not have access to,
   * unless the user is an admin, in which case no filtering is performed.
   *
   * @return {void} This method does not return a value; it modifies the `toolbarItems` property in place.
   */
  filterToolbarItems(): void {
    if (this.toolbarItems && !this.activeUserStore.user()?.isAdmin) {
      this.toolbarItems = this.toolbarItems.filter(toolbarItem => {
        switch (toolbarItem.key) {
          case 'entityAdder':
            return this.activeUserStore.canAccessEntityAdder();
          case 'userManagement':
            return this.activeUserStore.canAccessUserManagement();
          case 'config':
            return this.activeUserStore.canAccessConfig();
          case 'indicators':
            return this.activeUserStore.canAccessIndicators();
          case 'pool':
            return this.activeUserStore.canAccessPool();
          case 'themes':
            return this.activeUserStore.canAccessTopics();
          case 'bookmarks':
            return this.activeUserStore.canAccessBookmarks();
          case 'stories':
            return this.activeUserStore.canAccessStories();
          case 'colorsets':
            return this.activeUserStore.canAccessColorsets();
          case 'importExport':
            return this.activeUserStore.canAccessImportExport();
          case 'massActions':
            return this.activeUserStore.canAccessMassActions();
          case 'history':
            return this.activeUserStore.canAccessHistory();
          default:
            return false;
        }
      });
    }
  }
}
